import { comparer, type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { debounce } from 'lodash'
import { uiStore } from 'shared/store/uiStore'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { CalendarFilterEnum } from 'shared/ui/CalendarDropdown/calendarEnum'
import { DayjsFormats } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IAnalyticsParams } from 'entities/Analytics/api/types'
import { type SearchTypesEnum } from 'widgets/SearchDropdown'
import { ChartFilterStore } from 'pages/analytics/ui/ChartFilter'
import { BreakdownFilterStore } from 'pages/analytics/ui/BreakdownFilter/store/breakdownFilterStore'

interface AnalyticsFiltersCallbacks {
  onFiltersChange: (loadingType: 'page' | 'chart') => void
  additionalFilterTypes: SearchTypesEnum[]
  breakdownFilterTypes: SearchTypesEnum[]
}

export class AnalyticsFiltersStore {
  private readonly _calendarFilterStore: CalendarFilterStore
  private readonly _breakdownFilterStore: BreakdownFilterStore
  private readonly _chartFilterStore = new ChartFilterStore()

  private _disposePageFiltersReaction: IReactionDisposer | null = null
  private _disposeChartFiltersReaction: IReactionDisposer | null = null

  private _debouncedPageFiltersChange: ReturnType<typeof debounce>
  private _debouncedChartFiltersChange: ReturnType<typeof debounce>
  private _cancelDebouncedPageFiltersChange: () => void
  private _cancelDebouncedChartFiltersChange: () => void

  constructor(private _entities: IEntitiesStore, private _config: AnalyticsFiltersCallbacks) {
    this._calendarFilterStore = new CalendarFilterStore(
      this._entities.usersStore.user?.createdAt || null
    )
    this._calendarFilterStore.setSelectedCalendarFilter(CalendarFilterEnum.last30days)
    this._calendarFilterStore.setParamsDate()

    this._breakdownFilterStore = new BreakdownFilterStore(
      {
        additionalFilterTypes: this._config.additionalFilterTypes,
        breakdownFilterTypes: this._config.breakdownFilterTypes,
      },
      this._entities
    )

    this._debouncedPageFiltersChange = debounce(() => this._config.onFiltersChange('page'), 500)
    this._debouncedChartFiltersChange = debounce(() => this._config.onFiltersChange('chart'), 500)
    this._cancelDebouncedPageFiltersChange = this._debouncedPageFiltersChange.cancel
    this._cancelDebouncedChartFiltersChange = this._debouncedChartFiltersChange.cancel

    makeAutoObservable(this)
  }

  initReactions = () => {
    this._disposePageFiltersReaction?.()
    this._disposeChartFiltersReaction?.()

    this._disposePageFiltersReaction = reaction(
      () => ({
        dateFrom: this._calendarFilterStore.sendDate?.from
          ? this._calendarFilterStore.sendDate.from.getTime()
          : null,
        dateTo: this._calendarFilterStore.sendDate?.to
          ? this._calendarFilterStore.sendDate.to.getTime()
          : null,
        appliedParams: this._breakdownFilterStore.appliedParams,
      }),
      () => {
        this._debouncedPageFiltersChange()
      },
      { equals: comparer.structural }
    )

    this._disposeChartFiltersReaction = reaction(
      () => this.chartDataFilterParams,
      () => {
        this._debouncedChartFiltersChange()
      }
    )
  }

  get analyticsFiltersParams(): IAnalyticsParams {
    const { tags, teams, members, numbers, outboundSources, timeRange } =
      this._breakdownFilterStore.appliedParams

    return {
      from: this._calendarFilterStore.sendDate?.from
        ? uiStore
            .dayjsWithoutTimezone(this._calendarFilterStore.sendDate.from)
            .format(DayjsFormats.timestampServerDate)
        : '',
      to: this._calendarFilterStore.sendDate?.to
        ? uiStore
            .dayjsWithoutTimezone(this._calendarFilterStore.sendDate.to)
            .format(DayjsFormats.timestampServerDate)
        : '',
      timezone: uiStore.timezone,
      interval: this._chartFilterStore.period,
      filter: {
        tags: tags.map(String),
        inboxes: teams.map(String),
        members: members.map(String),
        numbers: numbers.map(String),
        sources: outboundSources.map(String),
      },
      ...(timeRange &&
        timeRange.fromTime &&
        timeRange.toTime && {
          hour_from: uiStore.dayjsWithoutTimezone(timeRange.fromTime).format(DayjsFormats.time3),
          hour_to: uiStore.dayjsWithoutTimezone(timeRange.toTime).format(DayjsFormats.time3),
        }),
    }
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get breakdownFilterStore() {
    return this._breakdownFilterStore
  }

  get chartFilterStore() {
    return this._chartFilterStore
  }

  get chartDataFilterParams() {
    return {
      period: this._chartFilterStore.period,
    }
  }

  dispose = () => {
    this._disposePageFiltersReaction?.()
    this._disposeChartFiltersReaction?.()
    this._cancelDebouncedPageFiltersChange()
    this._cancelDebouncedChartFiltersChange()
    this._breakdownFilterStore.dispose()
    this._chartFilterStore.dispose()
  }
}
